import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AdminPageProps, ProductType } from "@/types";
import { DialogProps } from "@/types/ui";
import { InertiaFormProps, useForm, usePage } from "@inertiajs/react";
import { FormEvent, useEffect, useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import CatalogSearchField from "./catalog-search-field";
import {
  api_part_out_set_path,
  part_out_set_store_products_path,
} from "@/lib/routes";
import { csrfToken } from "@/lib/api";
import DataTable from "./data-table";
import { ColumnDef } from "@tanstack/react-table";
import ImageWithFallback from "./image-with-fallback";
import useFieldArray from "@/hooks/use-field-array";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { getCurrencySymbol } from "@/lib/utils";
import useTranslations from "@/hooks/use-translations";

type PartOutSetFormData = {
  products: {
    product_type: ProductType;
    system_code: string;
    name: string;
    image_url: string | null;
    price: number | null;
    quantity: number;
  }[];
};

type PartOutSetMinifigureResult = {
  quantity: number;
  item: {
    system_code: string;
    name: string;
    image_url: string | null;
  };
};

function MinifiguresTable({
  form,
  minifigures,
}: {
  form: InertiaFormProps<PartOutSetFormData>;
  minifigures: PartOutSetFormData["products"];
}) {
  const { current_store } = usePage<AdminPageProps>().props;
  const { t } = useTranslations("shared.components.part_out_set_dialog");

  const { update } = useFieldArray(form, "products");

  const columns: ColumnDef<PartOutSetFormData["products"][0]>[] = useMemo(
    () => [
      {
        id: "image",
        header: t("minifigure_column"),
        cell({ row }) {
          return (
            <ImageWithFallback
              srcset={[row.original.image_url]}
              alt={row.original.name}
              className="size-24 object-contain"
            />
          );
        },
      },
      {
        id: "name",
        header: t("shared.fields.name"),
        cell({ row }) {
          return (
            <>
              <p className="text-sm font-medium">{row.original.name}</p>
              <p className="text-sm">{row.original.system_code}</p>
            </>
          );
        },
      },
      {
        accessorKey: "price",
        header: t("shared.fields.price"),
        cell({ row }) {
          return (
            <InputGroup>
              <InputGroupAddon>
                <InputGroupText>
                  {getCurrencySymbol(current_store?.currency ?? "usd")}
                </InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                id="price"
                name="product[price]"
                type="number"
                step="0.01"
                placeholder="0.00"
                defaultValue={row.getValue("price")}
                onChange={(e) =>
                  update(row.index, { price: e.target.valueAsNumber })
                }
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText>
                  {current_store?.currency.toUpperCase()}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          );
        },
      },
      {
        accessorKey: "quantity",
        header: t("shared.fields.quantity"),
        cell({ row }) {
          return (
            <Input
              id="quantity"
              type="number"
              step="1"
              className="w-16"
              defaultValue={row.getValue("quantity")}
              onChange={(event) =>
                update(row.index, { quantity: event.target.valueAsNumber })
              }
            />
          );
        },
      },
    ],
    [current_store?.currency]
  );

  return (
    <DataTable<PartOutSetFormData["products"][0]>
      columns={columns}
      data={minifigures}
    />
  );
}

export default function PartOutSetDialog({ open, onOpenChange }: DialogProps) {
  const { current_store } = usePage<AdminPageProps>().props;
  const { t } = useTranslations("shared.components.part_out_set_dialog");
  const partOutSetForm = useForm<PartOutSetFormData>({
    products: [],
  });

  async function onSelectedSystemCode(systemCode: string) {
    const response = await fetch(
      api_part_out_set_path({
        format: "json",
        params: { system_code: systemCode },
      }),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      }
    ).then((response) => response.json());

    if (response[0]) {
      response[0].items.forEach(
        (minifigure: PartOutSetMinifigureResult, index: number) => {
          partOutSetForm.setData(`products.${index}`, {
            product_type: "minifigure",
            system_code: minifigure.item.system_code,
            name: minifigure.item.name,
            image_url: minifigure.item.image_url,
            price: null,
            quantity: minifigure.quantity,
          });
        }
      );
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    partOutSetForm.post(part_out_set_store_products_path(current_store!.id), {
      invalidateCacheTags: ["stores", current_store!.id ?? ""],
      onSuccess() {
        partOutSetForm.resetAndClearErrors();
        onOpenChange(false);
      },
    });
  }

  useEffect(() => {
    if (!open && partOutSetForm.isDirty) {
      partOutSetForm.resetAndClearErrors();
    }
  }, [open, partOutSetForm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-10/12 grid-rows-[1fr]">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>

        <form
          id="part-out-set-form"
          onSubmit={onSubmit}
          className="flex-1 flex flex-col gap-5 overflow-x-hidden overflow-y-auto"
        >
          <CatalogSearchField
            productType="set"
            onSelectedSystemCode={(newValues) =>
              onSelectedSystemCode(newValues.system_code)
            }
          />

          {partOutSetForm.data.products.length > 0 && (
            <MinifiguresTable
              form={partOutSetForm}
              minifigures={partOutSetForm.data.products}
            />
          )}
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={partOutSetForm.processing}>
              {t("shared.actions.cancel")}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            form="part-out-set-form"
            disabled={partOutSetForm.processing}
          >
            {partOutSetForm.processing && <Spinner />}
            {t("submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
